/**
 * File upload field component with drag-and-drop support.
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { Upload, FileText, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { FieldWrapper, getFieldAriaIds } from "./FieldWrapper";
import type { FieldProps } from "./types";

export function FileField({
  field,
  value,
  touched,
  error,
  label,
  description,
  extendedDescription,
  onChange,
  onBlur,
}: FieldProps) {
  const { t } = useTranslation();
  const hasError = touched && !!error;
  const fileValue = value as File | null;
  const maxSizeMB = field.maxFileSize
    ? Math.round(field.maxFileSize / 1048576)
    : 0;
  const { descriptionId, extendedDescriptionId, errorId } = getFieldAriaIds(field.name);

  // Build aria-describedby based on available content
  const ariaDescribedByParts: string[] = [];
  if (hasError) {
    ariaDescribedByParts.push(errorId);
  } else {
    if (description) ariaDescribedByParts.push(descriptionId);
    if (extendedDescription) ariaDescribedByParts.push(extendedDescriptionId);
  }
  const ariaDescribedBy = ariaDescribedByParts.length > 0 ? ariaDescribedByParts.join(" ") : undefined;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      // Validate file type
      if (field.accept) {
        const acceptedTypes = field.accept
          .split(",")
          .map((t) => t.trim().toLowerCase());
        const fileExt = `.${file.name.split(".").pop()?.toLowerCase()}`;
        if (!acceptedTypes.some((t) => t === fileExt)) {
          onChange(field.name, null);
          return;
        }
      }
      // Validate file size
      if (field.maxFileSize && file.size > field.maxFileSize) {
        onChange(field.name, null);
        return;
      }
      onChange(field.name, file);
    } else {
      onChange(field.name, null);
    }
  };

  const handleRemoveFile = () => {
    onChange(field.name, null);
    // Reset the input
    const input = document.getElementById(field.name) as HTMLInputElement;
    if (input) input.value = "";
  };

  return (
    <FieldWrapper
      name={field.name}
      label={label}
      required={field.validation?.required}
      hasError={hasError}
      error={error}
      description={description}
      extendedDescription={extendedDescription}
    >
      {!fileValue ? (
        <div
          className={cn(
            "relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors",
            "hover:border-primary/50 hover:bg-primary/5",
            hasError ? "border-destructive" : "border-muted-foreground/25"
          )}
        >
          <Upload className="h-10 w-10 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground mb-1">
            {t("common.dragAndDrop", "Click to upload or drag and drop")}
          </p>
          <p className="text-xs text-muted-foreground">
            {field.accept?.toUpperCase().replace(/\./g, "")}{" "}
            {maxSizeMB > 0 && t("common.maxFileSize", "(max {{max}}MB)", { max: maxSizeMB })}
          </p>
          <input
            id={field.name}
            type="file"
            accept={field.accept}
            onChange={handleFileChange}
            onBlur={() => onBlur(field.name)}
            aria-invalid={hasError}
            aria-describedby={ariaDescribedBy}
            aria-required={field.validation?.required}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
      ) : (
        <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/50">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-primary" />
            <div>
              <p className="text-sm font-medium truncate max-w-[200px]">
                {fileValue.name}
              </p>
              <p className="text-xs text-muted-foreground">
                {(fileValue.size / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleRemoveFile}
            aria-label={t("common.removeFile", "Remove file")}
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      )}
    </FieldWrapper>
  );
}
